/**
 * Streaming XLSX writer — bade reports (2.5 lakh+ rows × 40+ columns) ke liye.
 *
 * ExcelJS ka browser bundle streaming support nahi karta (WorkbookWriter sirf
 * Node build mein hai), isliye wo pura workbook RAM mein banata hai aur
 * ~10 lakh+ cells par tab crash ho jaata hai.
 *
 * Yeh writer sheet XML ko chunks mein generate karta hai aur seedha fflate ke
 * streaming ZIP mein deflate karta chalta hai — memory sirf ek chunk (~1 MB)
 * jitni lagti hai, chahe rows kitni bhi hon.
 *
 * DOM ka koi use nahi hai, isliye yeh Web Worker aur main thread dono par
 * chalta hai.
 */

import { Zip, ZipDeflate, strToU8 } from "fflate";

export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export type HAlign =
  | "general"
  | "left"
  | "center"
  | "right"
  | "justify"
  | "fill"
  | "centerContinuous"
  | "distributed";

/** Ek cell ki value — main thread par hi parse ho chuki hoti hai. */
export type CellValue = string | number | null | undefined;

/** Table ke upar aane wali merged heading lines. */
export interface TitleLine {
  text: string;
  /** title = company name (16pt), address = 10pt grey, report = report name (14pt) */
  kind: "title" | "address" | "report";
}

export interface SheetMeta {
  sheetName?: string;
  /** Table header row. */
  headers: string[];
  /** Excel width units, headers ke same order mein. */
  colWidths: number[];
  /** Per-column horizontal alignment. */
  colAligns: (HAlign | undefined)[];
  /** Merged heading lines (company name, address, report name). */
  titleLines?: TitleLine[];
  /** Table header se pehle left-aligned grey lines. */
  leftHeaders?: string[];
  /** Wahi rows, last column mein right-aligned. */
  rightHeaders?: string[];
  /** Opening balance type lines — last column, right-aligned. */
  headerData?: string[];
  /** Closing balance type lines — data ke baad, last column, right-aligned. */
  footerData?: string[];
  /** Header row ke upar freeze pane lagayein. */
  freezeHeader?: boolean;
  /** Header row par auto-filter dropdowns. */
  autoFilter?: boolean;
  /**
   * Deflate level 0-9. 4 default hai: level 6 ke ~85% compression par
   * lagbhag do guna tez — 2.5 lakh rows par yeh minute-level farq hai.
   */
  compressionLevel?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
}

// ── XML escaping ────────────────────────────────────────────────────────────
// Fast path: 90%+ cell values mein escape karne layak kuch hota hi nahi, isliye
// pehle test karo, tabhi replace chalao.
// XML 1.0 mein tab/LF/CR ke alawa control chars illegal hain — na hataye to
// Excel poori file ko "unreadable content" bol deta hai.
const ILLEGAL_XML = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;
const NEEDS_ESCAPE = /[&<>\u0000-\u0008\u000B\u000C\u000E-\u001F]/;
const ESCAPE_MAP: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;" };

const escapeXml = (s: string): string => {
  if (!NEEDS_ESCAPE.test(s)) return s;
  return s.replace(/[&<>]/g, (c) => ESCAPE_MAP[c]).replace(ILLEGAL_XML, "");
};

// ── Column letters (A, B, ... AA, AB ...) ───────────────────────────────────
const colLetter = (n: number): string => {
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = (n - m - 1) / 26;
  }
  return s;
};

// ── Styles ──────────────────────────────────────────────────────────────────
const NUMFMT_DECIMAL = 164; // #,##0.00
const NUMFMT_PERCENT = 165; // 0.00%

// Fixed cellXfs indices
const S_TITLE = 1;
const S_ADDRESS = 2;
const S_REPORT = 3;
const S_TABLE_HEADER = 4;
const S_GREY_LEFT = 5;
const S_GREY_RIGHT = 6;

const FIXED_XFS = [
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>',
  '<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>',
  '<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>',
  '<xf numFmtId="0" fontId="3" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>',
  '<xf numFmtId="0" fontId="4" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>',
  '<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>',
  '<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>',
];

/**
 * Per-column style ids. Yeh sab pehle hi bana lete hain taki har cell par sirf
 * ek array lookup ho — 1 crore cells par per-cell object banana hi asli
 * bottleneck tha.
 */
interface StyleTable {
  xfs: string[];
  /** [colIndex] → style id */
  text: number[];
  num: number[];
  pct: number[];
  textBold: number[];
  numBold: number[];
  pctBold: number[];
}

const buildStyleTable = (colAligns: (HAlign | undefined)[]): StyleTable => {
  const xfs = [...FIXED_XFS];
  const cache = new Map<string, number>();

  const xfFor = (align: HAlign, numFmtId: number, bold: boolean): number => {
    const key = `${align}|${numFmtId}|${bold ? 1 : 0}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;

    const alignAttr =
      align === "general" ? "" : ` horizontal="${align}"`;
    const xf =
      `<xf numFmtId="${numFmtId}" fontId="${bold ? 5 : 0}" fillId="0" borderId="0" xfId="0"` +
      ` applyFont="1" applyNumberFormat="1" applyAlignment="1">` +
      `<alignment${alignAttr} vertical="center"/></xf>`;

    const id = xfs.length;
    xfs.push(xf);
    cache.set(key, id);
    return id;
  };

  const text: number[] = [];
  const num: number[] = [];
  const pct: number[] = [];
  const textBold: number[] = [];
  const numBold: number[] = [];
  const pctBold: number[] = [];

  for (let i = 0; i < colAligns.length; i++) {
    const a = colAligns[i];
    // Alignment na mile to text left, number/percent right — purane export
    // jaisa hi behaviour.
    const textAlign: HAlign = a || "left";
    const numAlign: HAlign = a || "right";

    text.push(xfFor(textAlign, 0, false));
    num.push(xfFor(numAlign, NUMFMT_DECIMAL, false));
    pct.push(xfFor(numAlign, NUMFMT_PERCENT, false));
    textBold.push(xfFor(textAlign, 0, true));
    numBold.push(xfFor(numAlign, NUMFMT_DECIMAL, true));
    pctBold.push(xfFor(numAlign, NUMFMT_PERCENT, true));
  }

  return { xfs, text, num, pct, textBold, numBold, pctBold };
};

const buildStylesXml = (xfs: string[]): string =>
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="2">' +
  `<numFmt numFmtId="${NUMFMT_DECIMAL}" formatCode="#,##0.00"/>` +
  `<numFmt numFmtId="${NUMFMT_PERCENT}" formatCode="0.00%"/>` +
  "</numFmts>" +
  '<fonts count="6">' +
  '<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/><scheme val="minor"/></font>' +
  '<font><b/><sz val="16"/><color rgb="FF000000"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="10"/><color rgb="FF666666"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="14"/><color rgb="FF000000"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>' +
  "</fonts>" +
  '<fills count="4">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFF0F0F0"/><bgColor indexed="64"/></patternFill></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FF4169E1"/><bgColor indexed="64"/></patternFill></fill>' +
  "</fills>" +
  '<borders count="2">' +
  "<border><left/><right/><top/><bottom/><diagonal/></border>" +
  '<border><left style="thin"><color rgb="FF000000"/></left><right style="thin"><color rgb="FF000000"/></right>' +
  '<top style="thin"><color rgb="FF000000"/></top><bottom style="thin"><color rgb="FF000000"/></bottom><diagonal/></border>' +
  "</borders>" +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  `<cellXfs count="${xfs.length}">${xfs.join("")}</cellXfs>` +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

const CONTENT_TYPES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
  "</Types>";

const ROOT_RELS_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  "</Relationships>";

const WORKBOOK_RELS_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
  '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
  "</Relationships>";

/** Excel sheet names: max 31 chars, kuch chars allowed nahi. */
const safeSheetName = (name: string): string => {
  const cleaned = (name || "Report").replace(/[\\/*?:[\]]/g, " ").trim();
  return (cleaned || "Report").slice(0, 31);
};

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

/** Sheet XML ko itna jama karke hi zip mein push karte hain. */
const FLUSH_AT = 1 << 20; // 1 MB

export class XlsxStreamWriter {
  private readonly meta: SheetMeta;
  private readonly nCols: number;
  private readonly refs: string[];
  private readonly styles: StyleTable;
  private readonly lastColLetter: string;

  private zip: Zip;
  private sheetFile: ZipDeflate | null = null;
  private outChunks: BlobPart[] = [];
  private result: Promise<Blob>;
  private settle!: {
    resolve: (b: Blob) => void;
    reject: (e: Error) => void;
  };

  private buf = "";
  private rowNum = 0;
  private headerRowNum = 0;
  private merges: string[] = [];
  private finished = false;
  private aborted = false;

  /** Sheet mein ab tak likhi gayi data rows (progress ke liye). */
  public rowsWritten = 0;

  constructor(meta: SheetMeta) {
    this.meta = meta;
    this.nCols = Math.max(meta.headers.length, 1);
    this.refs = new Array(this.nCols);
    for (let i = 0; i < this.nCols; i++) this.refs[i] = colLetter(i + 1);
    this.lastColLetter = this.refs[this.nCols - 1];
    this.styles = buildStyleTable(
      meta.colAligns.length ? meta.colAligns : new Array(this.nCols).fill(undefined)
    );

    this.result = new Promise<Blob>((resolve, reject) => {
      this.settle = { resolve, reject };
    });
    // abort() par koi await na kar raha ho to bhi unhandled rejection na aaye.
    // Yeh alag branch hai — finish() ka awaiter phir bhi reject dekhega.
    this.result.catch(() => {});

    this.zip = new Zip((err, data, final) => {
      if (err) {
        this.settle.reject(err as unknown as Error);
        return;
      }
      // fflate ka Uint8Array<ArrayBufferLike> hai; BlobPart ko ArrayBuffer-backed
      // view chahiye — runtime par yeh hamesha wahi hai.
      if (data && data.length) this.outChunks.push(data as BlobPart);
      if (final) {
        const blob = new Blob(this.outChunks, { type: XLSX_MIME });
        this.outChunks = [];
        this.settle.resolve(blob);
      }
    });
  }

  /** Static parts + sheet ka prelude + saari heading rows likh do. */
  start(): void {
    const m = this.meta;

    this.addSmallFile("[Content_Types].xml", CONTENT_TYPES_XML);
    this.addSmallFile("_rels/.rels", ROOT_RELS_XML);
    this.addSmallFile(
      "xl/workbook.xml",
      XML_HEADER +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
        ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        `<sheets><sheet name="${escapeXml(
          safeSheetName(m.sheetName || "Report")
        )}" sheetId="1" r:id="rId1"/></sheets>` +
        "</workbook>"
    );
    this.addSmallFile("xl/_rels/workbook.xml.rels", WORKBOOK_RELS_XML);
    this.addSmallFile("xl/styles.xml", buildStylesXml(this.styles.xfs));

    // Sheet — yahi streaming part hai
    this.sheetFile = new ZipDeflate("xl/worksheets/sheet1.xml", {
      level: m.compressionLevel ?? 4,
    });
    this.zip.add(this.sheetFile);

    // Heading rows pehle count karo taaki freeze pane ka row number pata ho
    const headingRowCount = this.countHeadingRows();
    const headerRow = headingRowCount + 1;

    this.buf =
      XML_HEADER +
      '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
      ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
      this.buildSheetViews(headerRow) +
      '<sheetFormatPr defaultRowHeight="15"/>' +
      this.buildCols() +
      "<sheetData>";

    this.writeHeadingRows();
    this.writeTableHeader();
  }

  /**
   * Data rows likho. `rows` mein har cell pehle se `parseCellValue()` se guzri
   * honi chahiye — string ya number.
   */
  writeRows(rows: CellValue[][], bold = false): void {
    if (this.aborted || !this.sheetFile) return;

    const { text, num, pct, textBold, numBold, pctBold } = this.styles;
    const sText = bold ? textBold : text;
    const sNum = bold ? numBold : num;
    const sPct = bold ? pctBold : pct;
    const refs = this.refs;
    const nCols = this.nCols;

    let out = "";

    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const rn = ++this.rowNum;
      out += `<row r="${rn}">`;

      const len = row.length < nCols ? row.length : nCols;
      for (let c = 0; c < len; c++) {
        const v = row[c];
        if (v === null || v === undefined || v === "") continue;

        const ref = refs[c] + rn;

        if (typeof v === "number") {
          if (Number.isFinite(v)) {
            out += `<c r="${ref}" s="${sNum[c]}"><v>${v}</v></c>`;
          } else {
            out += `<c r="${ref}" s="${sText[c]}" t="inlineStr"><is><t>${v}</t></is></c>`;
          }
          continue;
        }

        // Percentage string ("−100.00%") → Excel % format (0-1 scale)
        if (v.charCodeAt(v.length - 1) === 37 /* % */) {
          const n = parseFloat(v.slice(0, -1).replace(/,/g, ""));
          if (!isNaN(n)) {
            out += `<c r="${ref}" s="${sPct[c]}"><v>${n / 100}</v></c>`;
            continue;
          }
        }

        const esc = escapeXml(v);
        // Leading/trailing space Excel warna kha jaata hai
        const preserve =
          esc.charCodeAt(0) === 32 || esc.charCodeAt(esc.length - 1) === 32
            ? ' xml:space="preserve"'
            : "";
        out += `<c r="${ref}" s="${sText[c]}" t="inlineStr"><is><t${preserve}>${esc}</t></is></c>`;
      }

      out += "</row>";
      this.rowsWritten++;
    }

    this.buf += out;
    if (this.buf.length >= FLUSH_AT) this.flush();
  }

  /** Footer lines + sheet close + zip finalize. Blob resolve karta hai. */
  async finish(): Promise<Blob> {
    if (this.aborted) throw new Error("Export cancelled");
    if (this.finished) return this.result;
    this.finished = true;

    const m = this.meta;

    // Filter sirf table tak — closing balance lines usme nahi aani chahiye
    const lastTableRow = this.rowNum;

    // Closing balance type lines — last column, right aligned
    if (m.footerData?.length) {
      for (const line of m.footerData) {
        this.writeLastColRow(line, S_GREY_RIGHT);
      }
    }

    this.buf += "</sheetData>";

    if (m.autoFilter !== false && this.headerRowNum > 0) {
      this.buf += `<autoFilter ref="A${this.headerRowNum}:${this.lastColLetter}${lastTableRow}"/>`;
    }
    if (this.merges.length) {
      this.buf += `<mergeCells count="${this.merges.length}">${this.merges.join(
        ""
      )}</mergeCells>`;
    }
    this.buf += "</worksheet>";

    // Aakhri chunk final flag ke saath
    const tail = strToU8(this.buf);
    this.buf = "";
    this.sheetFile!.push(tail, true);
    this.sheetFile = null;

    this.zip.end();
    return this.result;
  }

  /** Beech mein cancel — zip aur pending promise dono chhod do. */
  abort(): void {
    if (this.finished || this.aborted) return;
    this.aborted = true;
    this.buf = "";
    this.outChunks = [];
    try {
      this.zip.terminate();
    } catch {
      /* already ended */
    }
    this.settle.reject(new Error("Export cancelled"));
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private addSmallFile(path: string, xml: string): void {
    const f = new ZipDeflate(path, { level: 6 });
    this.zip.add(f);
    f.push(strToU8(xml), true);
  }

  private flush(): void {
    if (!this.buf || !this.sheetFile) return;
    this.sheetFile.push(strToU8(this.buf), false);
    this.buf = "";
  }

  private countHeadingRows(): number {
    const m = this.meta;
    const sideRows = Math.max(
      m.leftHeaders?.length || 0,
      m.rightHeaders?.length || 0
    );
    return (
      (m.titleLines?.length || 0) + sideRows + (m.headerData?.length || 0)
    );
  }

  private buildSheetViews(headerRow: number): string {
    if (this.meta.freezeHeader === false) {
      return '<sheetViews><sheetView tabSelected="1" workbookViewId="0"/></sheetViews>';
    }
    return (
      '<sheetViews><sheetView tabSelected="1" workbookViewId="0">' +
      `<pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/>` +
      '<selection pane="bottomLeft"/>' +
      "</sheetView></sheetViews>"
    );
  }

  private buildCols(): string {
    const w = this.meta.colWidths;
    let out = "<cols>";
    for (let i = 0; i < this.nCols; i++) {
      const width = w[i] && w[i] > 0 ? w[i] : 12;
      out += `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`;
    }
    return out + "</cols>";
  }

  /** Poori width par merged single-cell row. */
  private writeMergedRow(textVal: string, styleId: number): void {
    const rn = ++this.rowNum;
    this.buf +=
      `<row r="${rn}"><c r="A${rn}" s="${styleId}" t="inlineStr"><is><t>${escapeXml(
        textVal
      )}</t></is></c></row>`;
    if (this.nCols > 1) {
      this.merges.push(`<mergeCell ref="A${rn}:${this.lastColLetter}${rn}"/>`);
    }
  }

  /** Sirf last column mein value wali row. */
  private writeLastColRow(textVal: string, styleId: number): void {
    const rn = ++this.rowNum;
    this.buf +=
      `<row r="${rn}"><c r="${this.lastColLetter}${rn}" s="${styleId}" t="inlineStr">` +
      `<is><t>${escapeXml(textVal)}</t></is></c></row>`;
  }

  private writeHeadingRows(): void {
    const m = this.meta;

    for (const line of m.titleLines || []) {
      const style =
        line.kind === "title"
          ? S_TITLE
          : line.kind === "report"
          ? S_REPORT
          : S_ADDRESS;
      this.writeMergedRow(line.text, style);
    }

    // leftHeaders / rightHeaders — same rows par left aur right
    const left = m.leftHeaders || [];
    const right = m.rightHeaders || [];
    const sideRows = Math.max(left.length, right.length);
    for (let i = 0; i < sideRows; i++) {
      const rn = ++this.rowNum;
      let cells = "";
      if (left[i]) {
        cells += `<c r="A${rn}" s="${S_GREY_LEFT}" t="inlineStr"><is><t>${escapeXml(
          left[i]
        )}</t></is></c>`;
      }
      if (right[i] && this.nCols > 1) {
        cells += `<c r="${this.lastColLetter}${rn}" s="${S_GREY_RIGHT}" t="inlineStr">` +
          `<is><t>${escapeXml(right[i])}</t></is></c>`;
      }
      this.buf += `<row r="${rn}">${cells}</row>`;
    }

    // Opening balance type lines
    for (const line of m.headerData || []) {
      this.writeLastColRow(line, S_GREY_RIGHT);
    }
  }

  private writeTableHeader(): void {
    const rn = ++this.rowNum;
    this.headerRowNum = rn;
    let out = `<row r="${rn}">`;
    for (let i = 0; i < this.nCols; i++) {
      out += `<c r="${this.refs[i]}${rn}" s="${S_TABLE_HEADER}" t="inlineStr"><is><t>${escapeXml(
        this.meta.headers[i] ?? ""
      )}</t></is></c>`;
    }
    this.buf += out + "</row>";
  }
}

/**
 * Cell value ko Excel ke liye normalize karo.
 * Numeric strings ("1,23,456.00") → JS number, taki Excel mein SUM chale.
 * Percent strings jaise ke waise — writer unhe % format deta hai.
 */
export const parseCellValue = (val: any): CellValue => {
  if (val === undefined || val === null || val === "") return "";
  if (typeof val === "number") return Number.isFinite(val) ? val : String(val);

  const strVal = typeof val === "string" ? val.trim() : String(val).trim();
  if (strVal === "") return "";
  if (strVal.charCodeAt(strVal.length - 1) === 37 /* % */) return strVal;

  const cleaned = strVal.indexOf(",") === -1 ? strVal : strVal.replace(/,/g, "");
  if (!/^-?[\d.]+$/.test(cleaned)) return strVal;

  const num = Number(cleaned);
  return isNaN(num) ? strVal : num;
};

